// Minimal IndexedDB wrapper for persisting per-guest uploaded content.
//
// Why IndexedDB instead of localStorage:
//   - localStorage is synchronous and capped at ~5 MB per origin in most
//     browsers. The previous implementation serialized compressed image
//     data-URLs into localStorage, which hits the quota after only a few
//     uploads. When `setItem` throws QuotaExceededError, the previous code
//     silently dropped the write -- so on next page load, the guest's
//     gallery was empty even though their files had successfully uploaded
//     to the server. That is the "not all uploaded images appear" symptom.
//   - IndexedDB has much larger quotas (typically tens of MB to GB
//     depending on the browser) and is async.
//
// We intentionally do NOT add the `idb` npm dep to keep the bundle slim.

const DB_NAME = 'event-guest-store';
const DB_VERSION = 1;
const STORE_NAME = 'uploadedContent';

let dbPromise = null;

const isBrowser = typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const openDb = () => {
  if (!isBrowser) {
    return Promise.reject(new Error('IndexedDB is not available in this environment'));
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });

  return dbPromise;
};

const runTransaction = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    let result;
    try {
      result = fn(store);
    } catch (err) {
      reject(err);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction error'));
  });
};

export const idbGet = async (key) => {
  try {
    return await runTransaction('readonly', (store) => {
      return new Promise((resolve, reject) => {
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    });
  } catch (err) {
    // Fail closed: caller treats missing/broken store as "no cached content".
    return undefined;
  }
};

export const idbSet = async (key, value) => {
  try {
    await runTransaction('readwrite', (store) => {
      store.put(value, key);
    });
    return true;
  } catch (err) {
    return false;
  }
};

export const idbDelete = async (key) => {
  try {
    await runTransaction('readwrite', (store) => {
      store.delete(key);
    });
    return true;
  } catch (err) {
    return false;
  }
};

export const isIdbAvailable = () => isBrowser;
